import { ReactNode } from 'react';

import { Field } from 'react-final-form';

import { MultiAutocompleteV2 } from '@cast/design-system';
import type { MultiAutocompleteV2Props } from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { showErrorOnChange } from '../utils';

type OmitMultiAutocompleteV2Props<Item> = Omit<
  MultiAutocompleteV2Props<Item>,
  'value'
>;

type Props<Item> = {
  name: string;
  renderError?: (error: string | string[]) => ReactNode;
} & OmitMultiAutocompleteV2Props<Item>;

/** @public */
export const RffMultiAutocompleteV2 = <Item,>({
  disabled: propsDisabled,
  readOnly: propsReadOnly,
  name: propsName,
  onChange: propsOnChange,
  renderError,
  ...props
}: Props<Item>) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: propsDisabled,
    readOnly: propsReadOnly ?? mode === 'view',
    name: propsName,
  });

  return (
    <Field<string[]> name={name || propsName}>
      {({ input: { onChange, value }, meta }) => (
        // @ts-expect-error todo: come up with a better type solution
        <MultiAutocompleteV2
          {...(props as unknown as OmitMultiAutocompleteV2Props<Item>)}
          value={value || []}
          onChange={({ values, selectedItems }) => {
            onChange(values);
            propsOnChange?.({ values, selectedItems });
          }}
          disabled={disabled}
          readOnly={readOnly}
          success={meta.touched && meta.valid}
          error={
            showErrorOnChange(meta)
              ? renderError?.(meta.error) || meta.error
              : undefined
          }
        />
      )}
    </Field>
  );
};
